import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiHome, FiPlus, FiEdit2, FiTrash2, FiArrowRight, FiEye } from 'react-icons/fi';

export default function UnitsIndex({ units_by_facility = {}, facilities = [], user }) {
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };
  
  // Format room type for display
  const formatRoomType = (roomType) => {
    return roomType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Filter units by selected facility
  const filteredFacilities = selectedFacility === 'all' 
    ? Object.entries(units_by_facility)
    : Object.entries(units_by_facility).filter(([facility]) => facility.id.toString() === selectedFacility);
  
  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Units</h1>
              <p className="text-gray-500">Manage your facility units and availability</p>
            </div>
            <Button className="mt-4 md:mt-0 flex items-center" asChild>
              <Link href="/facility_owner/units/new">
                <FiPlus className="mr-2 h-4 w-4" />
                Add New Unit
              </Link>
            </Button>
          </div>
          
          {/* Facility filter */}
          <div className="mb-6">
            <h2 className="text-lg font-medium mb-2">Filter by Facility</h2>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant={selectedFacility === 'all' ? "default" : "outline"} 
                size="sm"
                onClick={() => setSelectedFacility('all')}
              >
                All Facilities
              </Button>
              
              {facilities.map(facility => (
                <Button
                  key={facility.id}
                  variant={selectedFacility === facility.id.toString() ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedFacility(facility.id.toString())}
                >
                  {facility.name}
                </Button>
              ))}
            </div>
          </div>
          
          {filteredFacilities.length > 0 ? (
            <div className="space-y-8">
              {filteredFacilities.map(([facility, units]) => (
                <Card key={facility.id} className="overflow-hidden">
                  <CardHeader className="bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="flex items-center">
                          <FiHome className="mr-2 h-5 w-5 text-blue-600" />
                          {facility.name}
                        </CardTitle>
                        <CardDescription>
                          {units.length} unit type{units.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <Link href={`/facility_owner/facilities/${facility.id}`}>
                          <FiEye className="mr-2 h-4 w-4" />
                          View Facility
                        </Link>
                      </Button>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bedrooms</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bathrooms</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Square Ft</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Price</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {units.map(unit => (
                            <tr key={unit.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900">
                                  {formatRoomType(unit.room_type)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                {unit.bedrooms}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                {unit.bathrooms}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                {unit.square_feet ? `${unit.square_feet} sq ft` : 'Not specified'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                {formatCurrency(unit.monthly_cost_cents)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge variant={unit.available_count > 0 ? "success" : "secondary"}>
                                  {unit.available_count}
                                </Badge>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end space-x-2">
                                  <Button variant="outline" size="sm" className="flex items-center" asChild>
                                    <Link href={`/facility_owner/units/${unit.id}/edit`}>
                                      <FiEdit2 className="h-4 w-4" />
                                    </Link>
                                  </Button>
                                  
                                  <Link 
                                    href={`/facility_owner/units/${unit.id}`} 
                                    method="delete" 
                                    as="button"
                                    className="inline-flex items-center justify-center h-8 px-2 py-1 text-sm font-medium border rounded-md border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                                  >
                                    <FiTrash2 className="h-4 w-4 text-red-500" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                  <CardFooter className="bg-gray-50 flex justify-end py-3">
                    <Button asChild>
                      <Link href={`/facility_owner/units/new?facility_id=${facility.id}`}>
                        <FiPlus className="mr-2 h-4 w-4" />
                        Add Unit to {facility.name}
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <FiHome className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No units found</h3>
                <p className="text-gray-500 mb-4">
                  {selectedFacility !== 'all' 
                    ? "No units for the selected facility." 
                    : "Get started by adding your first unit."}
                </p>
                <Button asChild>
                  <Link href={selectedFacility !== 'all' 
                    ? `/facility_owner/units/new?facility_id=${selectedFacility}` 
                    : "/facility_owner/units/new"}>
                    <FiPlus className="mr-2 h-4 w-4" />
                    Add New Unit
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
